import { Either } from 'monet';
import LeftResponse from '@/storage/LeftResponse';
import { ProductCategory, ProductUnit } from '@/storage/gateways/ProductGateway';

/**
 * @description Запрос поступления.
 * @param {string|null} id
 * @param {string} vendor
 * @param {ArrivalProduct[]} products
 * @param {Date} arrivalDate
 * @constructor
 */
export function ArrivalRequest(
  id = null,
  vendor = '',
  products = [],
  arrivalDate = new Date(),
) {
  this.getId = () => id;
  this.toQuery = () => ({
    vendor,
    products: products.map((product) => ({
      id: product.id,
      price: product.price,
      count: product.count,
    })),
    arrivalDate,
  });
}

/**
 * @description Товар поступления.
 * @param {number} index  Индекс.
 * @param {number} id     Идентификатор товара.
 * @param {string} name   Название товара.
 * @param {number} count  Количество единиц.
 * @param {string} unit   Единица измерения.
 * @param {number} price  Цена.
 * @constructor
 */
export function ArrivalProduct(
  index,
  id,
  name = '',
  count = 1,
  unit = '',
  price = 0,
) {
  this.name = name;
  this.id = id;
  this.count = count;
  this.index = index;
  this.unit = unit;
  this.price = price;
  const finalPrice = count * price;
  this.getFinalPrice = () => finalPrice;
  this.setPrice = (newPrice) => new this
    .constructor(index, id, name, count, unit, newPrice);
  this.setCount = (value) => new this
    .constructor(index, id, name, value, unit, price);
}

/**
 * @description Поступление.
 * @param {string} id
 * @param {string} vendor
 * @param {ArrivalProduct[]} products
 * @param {Date} arrivalDate
 * @param {string} createdDateTitle
 * @constructor
 */
export function Arrival(
  id,
  vendor,
  products,
  arrivalDate = new Date(),
  createdDateTitle = '',
) {
  this.id = id;
  this.vendor = vendor;
  this.products = products;
  this.createdDateTitle = createdDateTitle;
  this.arrivalDate = arrivalDate;
}

/**
 * @description Шлюз поступлений.
 * @param {Axios} axios HTTP клиент.
 * @constructor
 */
export default function ArrivalGateway(axios) {
  /**
   * @description Просмотр поступления.
   * @param {string} id
   * @returns {Promise<Either>}
   */
  this.viewArrival = (id) => axios
    .get(`/api/warehouse/arrivals/${id}`)
    .then(
      async (response) => Either.right({
        arrival: new Arrival(
          response.data.id,
          response.data.vendor_id,
          response.data.products.map((item, index) => (new ArrivalProduct(
            index,
            item.product_id,
            item.product_name,
            item.count,
            item.product_unit,
            item.price,
          ))),
          response.data.created_date_title,
          response.data.created_date_title,
        ),
      }),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Новое поступление.
   * @param {Object} params
   * @returns {Promise<Either>}
   */
  this.create = (params) => axios
    .post('/api/warehouse/arrivals', params)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Удаление поступления.
   * @param {string} id Идентификатор поступления.
   * @returns {Promise<Either>}
   */
  this.delete = (id) => axios
    .delete(`/api/warehouse/arrivals/${id}`)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Редактирование поступления.
   * @param {string} id
   * @param {Object} params
   * @returns {Promise<Either>}
   */
  this.edit = (id, params) => axios
    .put(`/api/warehouse/arrivals/${id}`, params)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Получить все поступления.
   * @param {number} page Номер страницы.
   * @returns {Promise<Either>}
   */
  this.getAll = (page = 1) => axios
    .get('/api/warehouse/arrivals', { params: { page } })
    .then(
      async (response) => {
        const { data } = response;
        const pageCount = data.page_count;
        const arrivals = data.arrivals.map((item) => ({
          id: item.id,
          number: item.number,
          date: item.date,
          vendor: item.vendor,
          amount: item.amount,
          author: item.author,
          products: item.products.map((product, index) => ({
            index,
            name: product.name,
            count: product.count,
            unit: product.unit,
            amount: product.amount,
          })),
        }));
        return Either.right({
          arrivals,
          pageCount,
        });
      },
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Показать опции.
   * @returns {Promise<Either>}
   */
  this.viewOptions = () => axios
    .get('/api/warehouse/arrival/options')
    .then(
      async (response) => {
        const vendors = response.data.vendors.map((item) => ({
          id: item.id,
          name: item.name,
        }));
        const products = response.data.products.map((item) => ({
          id: item.id,
          name: item.name,
          unit: item.unit,
        }));
        const categories = response.data.categories.map((item) => (new ProductCategory(
          item.id,
          item.name,
        )));
        const units = response.data.units.map((item) => (new ProductUnit(
          item.id,
          item.name,
          item.is_pack,
        )));
        return Either.right({
          vendors,
          products,
          categories,
          units,
        });
      },
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
}
